import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import {
  TrendingUp,
  Scale,
  Flame,
  Dumbbell,
  ChevronRight,
  Plus,
  ArrowDown,
  ArrowUp,
  Minus,
  Calendar,
  UtensilsCrossed,
  CheckCircle2,
} from "lucide-react";
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  isWithinInterval,
} from "date-fns";
import { QuickProgressDialog } from "./QuickProgressLog";

const KG_PER_LB = 0.45359237;

function calcStreak(checkIns, userId) {
  if (!checkIns.length) return 0;
  const userCheckins = checkIns.filter((c) => c.user_id === userId);
  if (!userCheckins.length) return 0;
  const checkedDates = new Set(
    userCheckins.map((c) => c.checked_in_date).filter(Boolean)
  );
  if (!checkedDates.size) return 0;
  let streak = 0;
  let current = new Date();
  const todayKey = format(current, "yyyy-MM-dd");
  if (!checkedDates.has(todayKey)) {
    current = subDays(current, 1);
  }
  while (true) {
    const key = format(current, "yyyy-MM-dd");
    if (checkedDates.has(key)) {
      streak++;
      current = subDays(current, 1);
    } else {
      break;
    }
  }
  return streak;
}

export default function YourProgressSection({
  upcomingSessionsCount = 0,
  completedSessionsCount = 0,
  activeWorkoutsCount = 0,
  activeMealsCount = 0,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logModalOpen, setLogModalOpen] = useState(false);

  const useLbs = user?.use_lbs ?? true;
  const weightUnit = useLbs ? "lbs" : "kg";

  // 1. Fetch Progress logs
  const { data: logs = [] } = useQuery({
    queryKey: ["progress"],
    queryFn: () => base44.entities.ProgressLog.list("-date", 30),
    enabled: !!user,
  });

  // 2. Fetch CheckIns for streak
  const { data: checkIns = [] } = useQuery({
    queryKey: ["checkins"],
    queryFn: () => base44.entities.CheckIn.list(),
  });

  // 3. Fetch weekly workout plan & completions
  const { data: weeklyPlan = null } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: user.id }).then(
        (results) => results[0] || null
      ),
    enabled: !!user,
  });

  const { data: completions = [] } = useQuery({
    queryKey: ["workout-completions-all", user?.id],
    queryFn: () =>
      base44.entities.WorkoutCompletion.filter({ user_id: user.id }),
    enabled: !!user,
  });

  // Calculate latest weight and delta
  const weightInfo = useMemo(() => {
    const sorted = [...logs].sort((a, b) => {
      const dateCmp = (b.date || "").localeCompare(a.date || "");
      if (dateCmp !== 0) return dateCmp;
      return (b.created_date || "").localeCompare(a.created_date || "");
    });
    const valid = sorted.filter(
      (l) => typeof l.weight_kg === "number" && !Number.isNaN(l.weight_kg)
    );
    if (!valid.length) return null;

    const latest = valid[0];
    const previous = valid[1];
    const latestWeight = useLbs
      ? latest.weight_kg / KG_PER_LB
      : latest.weight_kg;

    let delta = null;
    if (previous) {
      const prevWeight = useLbs
        ? previous.weight_kg / KG_PER_LB
        : previous.weight_kg;
      const diff = latestWeight - prevWeight;
      delta = {
        value: Math.abs(diff).toFixed(1),
        direction: Math.abs(diff) < 0.1 ? "flat" : diff < 0 ? "down" : "up",
      };
    }

    return {
      displayWeight: latestWeight.toFixed(1),
      delta,
      date: latest.date,
    };
  }, [logs, useLbs]);

  // Calculate current streak
  const currentStreak = useMemo(() => {
    return calcStreak(checkIns, user?.id);
  }, [checkIns, user?.id]);

  // Calculate weekly workout progress
  const workoutProgress = useMemo(() => {
    if (!weeklyPlan) return { scheduled: 0, completed: 0, percent: 0 };
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const scheduled = days.filter((d) => !!weeklyPlan[d]).length;

    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });

    const completedThisWeek = completions.filter((c) => {
      if (!c.completed_date) return false;
      try {
        const d = new Date(c.completed_date);
        return isWithinInterval(d, { start, end });
      } catch (_e) {
        return false;
      }
    }).length;

    const percent =
      scheduled > 0
        ? Math.min(100, Math.round((completedThisWeek / scheduled) * 100))
        : 0;

    return {
      scheduled: scheduled || 5, // fallback baseline if none configured
      completed: completedThisWeek,
      percent,
    };
  }, [weeklyPlan, completions]);

  return (
    <>
      <div
        data-testid="your-progress-section"
        className="rounded-3xl p-5 sm:p-6 text-white space-y-4 shadow-xl border border-white/10"
        style={{
          background: "radial-gradient(ellipse at top, #0E1B2F 0%, #070D18 100%)",
        }}
      >
        {/* HEADER: TITLE & VIEW ALL ACTION */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-sky-400">
              METRICS & MILESTONES
            </p>
            <h3 className="text-xl sm:text-2xl font-display font-black text-white tracking-tight">
              Your Progress
            </h3>
          </div>

          <Link
            to="/progress"
            data-testid="view-all-progress-btn"
            className="px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-bold text-sky-300 flex items-center gap-1.5 transition active:scale-95"
          >
            <span>View All</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-70" />
          </Link>
        </div>

        {/* 3 COMPACT PRIMARY METRIC TILES */}
        <div className="grid grid-cols-3 gap-2.5">
          {/* TILE 1: CURRENT WEIGHT */}
          <button
            type="button"
            onClick={() => setLogModalOpen(true)}
            data-testid="metric-weight-tile"
            className="p-3.5 rounded-2xl bg-white/[0.04] border border-white/5 hover:border-sky-400/40 text-left transition active:scale-98 group space-y-1.5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                Weight
              </span>
              <Plus className="w-3.5 h-3.5 text-sky-400 opacity-60 group-hover:opacity-100" />
            </div>

            <div>
              <p className="text-base sm:text-lg font-black font-display text-white">
                {weightInfo ? weightInfo.displayWeight : "--"}
                <span className="text-xs font-normal text-slate-400 ml-1">
                  {weightUnit}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-1 text-[10px]">
              {weightInfo?.delta ? (
                <span
                  className={`font-bold flex items-center gap-0.5 ${
                    weightInfo.delta.direction === "down"
                      ? "text-emerald-400"
                      : weightInfo.delta.direction === "up"
                      ? "text-amber-400"
                      : "text-slate-400"
                  }`}
                >
                  {weightInfo.delta.direction === "down" && (
                    <ArrowDown className="w-2.5 h-2.5" />
                  )}
                  {weightInfo.delta.direction === "up" && (
                    <ArrowUp className="w-2.5 h-2.5" />
                  )}
                  {weightInfo.delta.direction === "flat" && (
                    <Minus className="w-2.5 h-2.5" />
                  )}
                  {weightInfo.delta.value} {weightUnit}
                </span>
              ) : (
                <span className="text-slate-500 font-medium">Log entry</span>
              )}
            </div>
          </button>

          {/* TILE 2: CURRENT STREAK */}
          <div
            data-testid="metric-streak-tile"
            className="p-3.5 rounded-2xl bg-white/[0.04] border border-white/5 space-y-1.5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                Streak
              </span>
              <Flame className="w-3.5 h-3.5 text-amber-400" fill="currentColor" />
            </div>

            <div>
              <p className="text-base sm:text-lg font-black font-display text-white">
                {currentStreak}
                <span className="text-xs font-normal text-slate-400 ml-1">
                  {currentStreak === 1 ? "Day" : "Days"}
                </span>
              </p>
            </div>

            <p className="text-[10px] font-bold text-sky-400 uppercase tracking-tight truncate">
              {currentStreak > 0 ? "Active Streak" : "Check in today"}
            </p>
          </div>

          {/* TILE 3: WORKOUT PROGRESS */}
          <button
            type="button"
            onClick={() => navigate("/workouts")}
            data-testid="metric-workouts-tile"
            className="p-3.5 rounded-2xl bg-white/[0.04] border border-white/5 hover:border-sky-400/40 text-left transition active:scale-98 group space-y-1.5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                Workouts
              </span>
              <Dumbbell className="w-3.5 h-3.5 text-sky-400 opacity-60 group-hover:opacity-100" />
            </div>

            <div>
              <p className="text-base sm:text-lg font-black font-display text-white">
                {workoutProgress.completed}
                <span className="text-xs font-normal text-slate-400 ml-1">
                  / {workoutProgress.scheduled}
                </span>
              </p>
            </div>

            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden mt-1">
              <div
                className="h-full bg-gradient-to-r from-sky-400 to-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${workoutProgress.percent}%` }}
              />
            </div>
          </button>
        </div>

        {/* SECONDARY QUICK METRICS (Preserves all existing data points from previous dashboard) */}
        <div className="grid grid-cols-4 gap-2 pt-1 border-t border-white/5">
          <Link
            to="/sessions"
            className="p-2.5 rounded-xl bg-black/20 hover:bg-white/5 border border-white/5 text-center transition"
          >
            <p className="text-xs sm:text-sm font-extrabold text-sky-400">
              {upcomingSessionsCount}
            </p>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
              Upcoming
            </p>
          </Link>

          <Link
            to="/progress"
            className="p-2.5 rounded-xl bg-black/20 hover:bg-white/5 border border-white/5 text-center transition"
          >
            <p className="text-xs sm:text-sm font-extrabold text-emerald-400">
              {completedSessionsCount}
            </p>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
              Sessions
            </p>
          </Link>

          <Link
            to="/workouts"
            className="p-2.5 rounded-xl bg-black/20 hover:bg-white/5 border border-white/5 text-center transition"
          >
            <p className="text-xs sm:text-sm font-extrabold text-purple-400">
              {activeWorkoutsCount}
            </p>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
              Workouts
            </p>
          </Link>

          <Link
            to="/meals"
            className="p-2.5 rounded-xl bg-black/20 hover:bg-white/5 border border-white/5 text-center transition"
          >
            <p className="text-xs sm:text-sm font-extrabold text-amber-400">
              {activeMealsCount}
            </p>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
              Meal Plans
            </p>
          </Link>
        </div>
      </div>

      {/* QUICK PROGRESS LOG MODAL */}
      {logModalOpen && (
        <QuickProgressDialog
          open={logModalOpen}
          onOpenChange={setLogModalOpen}
          user={user}
        />
      )}
    </>
  );
}
